import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, BRAND_INFO } from '../data/mock';
import { HelpCircle, ChevronDown, Phone, Mail, MessageCircle } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const FAQPage = () => {
  usePageTitle('Frequently Asked Questions', 'Find answers to common questions about Stitch Your Dash products, shipping, returns, and more.');
  
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Orders & Shipping',
      questions: [
        {
          q: 'How long does shipping take?',
          a: 'Standard shipping takes 5-7 business days. Express shipping takes 2-3 business days. Orders placed before 3 PM EST are processed the same day.'
        },
        {
          q: 'Do you offer free shipping?',
          a: 'Yes! We offer free standard shipping on all orders over $99 within the United States.'
        },
        {
          q: 'Where do you ship to?',
          a: 'We currently ship to all 50 states within the United States.'
        },
        {
          q: 'How can I track my order?',
          a: 'Once your order ships, you will receive an email with tracking information. You can use this to track your package on the USPS website.'
        }
      ]
    },
    {
      category: 'Products',
      questions: [
        {
          q: 'Are your products suitable for beginners?',
          a: 'Absolutely! Many of our products are designed with beginners in mind. We offer easy-to-use tools that help you learn and grow your sewing skills.'
        },
        {
          q: 'What materials are your products made from?',
          a: 'Our products are made from high-quality materials including stainless steel, durable plastics, and premium fabrics. Check each product page for specific material information.'
        },
        {
          q: 'Do your sewing machines come with a warranty?',
          a: 'Yes, all our sewing machines and major equipment come with a manufacturer warranty. Please check the specific product for warranty details.'
        }
      ]
    },
    {
      category: 'Returns & Refunds',
      questions: [
        {
          q: 'What is your return policy?',
          a: 'We offer a 30-day return policy for unused items in their original packaging. Simply contact us to initiate a return.'
        },
        {
          q: 'How do I return an item?',
          a: 'Contact our customer service team at ' + CONTACT_INFO.email + ' or call ' + CONTACT_INFO.phone + ' to receive a return shipping label.'
        },
        {
          q: 'How long do refunds take?',
          a: 'Refunds are processed within 5-7 business days after we receive your returned item. The refund will appear on your original payment method.'
        }
      ]
    },
    {
      category: 'Payment & Security',
      questions: [
        {
          q: 'What payment methods do you accept?',
          a: 'We accept Visa, Mastercard, and American Express. Discover is not an accepted form of payment. All payments are processed securely using 256-bit SSL encryption.'
        },
        {
          q: 'Is my payment information secure?',
          a: 'Yes, we use SSL 256-bit encryption to protect your payment information. Your data is never stored on our servers.'
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Hero with Banner Image */}
        <section className="relative bg-warmBrown-800 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1604095879468-03fd4a4afaf2?w=1920&q=80"
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-warmBrown-900 via-warmBrown-900/90 to-warmBrown-800/70" />
          </div>
          <div className="absolute inset-0 pattern-dots opacity-20" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="max-w-2xl">
              <div className="w-16 h-16 bg-rose-500/20 rounded-2xl flex items-center justify-center mb-6">
                <HelpCircle className="w-8 h-8 text-rose-400" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4">Frequently Asked Questions</h1>
              <p className="text-warmBrown-300 text-lg">Find answers to common questions about our products, shipping, and policies.</p>
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {faqs.map((category, catIndex) => (
              <div key={catIndex} className="mb-10">
                <h2 className="font-display text-2xl text-warmBrown-900 mb-6">{category.category}</h2>
                <div className="space-y-3">
                  {category.questions.map((item, qIndex) => {
                    const isOpen = openIndex === `${catIndex}-${qIndex}`;
                    return (
                      <div key={qIndex} className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
                        <button
                          onClick={() => toggleQuestion(catIndex, qIndex)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-cream-50 transition-colors"
                        >
                          <span className="font-medium text-warmBrown-800 pr-4">{item.q}</span>
                          <ChevronDown className={`w-5 h-5 text-warmBrown-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5 text-warmBrown-600 leading-relaxed">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Contact CTA */}
            <div className="mt-12 bg-rose-50 rounded-3xl p-8 text-center border border-rose-100">
              <MessageCircle className="w-10 h-10 text-rose-500 mx-auto mb-4" />
              <h3 className="font-display text-xl text-warmBrown-900 mb-3">Still Have Questions?</h3>
              <p className="text-warmBrown-600 mb-6">Our customer service team is happy to help!</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium">
                  <Phone className="w-5 h-5" /> {CONTACT_INFO.phone}
                </a>
                <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium">
                  <Mail className="w-5 h-5" /> {CONTACT_INFO.email}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
